<template>
    <main class="dashboard">
        <MainLayout @change-section="onSectionChange" :currentSection="visibleSection"
            :chartLoaded="chartLoaded" />
        <div class="underHeader">
            <div v-if="has_conciliator" v-show="visibleSection == 'conciliador'" class="conciliador-container">
                <section class="dashboard-section consolidado-valores">
                    <div class="container is-half">
                        <SectionHeader title="Resumen de tu salud financiera"
                            description="Mira en línea tus cuentas por cobrar, por pagar y sus vencimientos en una proyección de caja." />
                        <Consolidation :isVisible="visibleSection == 'conciliador'"
                            @onChartRendered="onChartRendered" />
                    </div>
                </section>
                <DashboardMainTabs class="my-6">
                    <template v-slot:obligations>
                        <Obligations :isVisible="visibleSection == 'conciliador'" />
                    </template>
                    <template v-slot:stament>
                        <BankMovements />
                    </template>
                </DashboardMainTabs>
                <section class="is-hidden-tablet my-4">
                    <div class="notification is-info is-light py-5">
                        <h3 class="is-size-5 mb-2">Importante</h3>
                        <p>
                            Te recomendamos utilizar la versión de escritorio para
                            agregar nuevos documentos.
                        </p>
                    </div>
                </section>
            </div>
            <AccountsPlan v-if="has_classificator" v-show="visibleSection == 'accounts_plan'" />
            <Financiamiento v-if="has_financiamiento" v-show="isFinanciamientoSection" />
            <DashboardBasic v-show="visibleSection == 'reporte'" :isVisible="visibleSection == 'reporte'"
                :chartLoaded="chartLoaded" />
            <DashboardFileUpload v-if="has_file_upload" v-show="visibleSection == 'fileUpload'" />
            <DashboardClassificator v-if="visibleSection == 'classificator'" />
            <DashboardReports v-if="visibleSection == 'reports'" />
            <Configuracion v-if="visibleSection == 'configuracion'" />
            <div v-show="visibleSection == 'none'">
                <div class="notification is-info is-light py-5">
                    <h3 class="is-size-5 mb-2 has-text-weight-bold">Importante</h3>
                    <p>
                        Tu panel personalizado aún no está activo. Si necesitas orientación
                        para configurarlo o reactivar tu acceso, estamos aquí para ayudarte.
                        <br /><br />
                        ¡Escríbenos a
                        <a href="mailto:contacto@cforemoto.com" target="_blank" class="has-text-weight-bold">
                            contacto@cforemoto.com</a>
                        y descubre cómo aprovechar CFOremoto al máximo!
                    </p>
                </div>
            </div>
            <div v-show="visibleSection == ''" />

        </div>

    </main>
</template>

<script>
import BankMovements from "@/components/bankMovements/BankMovements";
import Consolidation from "@/components/dashboard/consolidation/Consolidation";
import DashboardMainTabs from "@/components/dashboardMainTabs/DashboardMainTabs";
import MainLayout from "@/components/layout/MainLayout";
import Obligations from "@/components/obligations/Obligations";
import SectionHeader from "@/components/sectionHeader/SectionHeader";
import AccountsPlan from "@/views/AccountsPlan";
import Configuracion from "@/views/Configuracion";
import DashboardBasic from "@/views/DashboardBasic";
import DashboardClassificator from "@/views/DashboardClassificator";
import DashboardFileUpload from "@/views/DashboardFileUpload";
import DashboardReports from "@/views/DashboardReports";
import Financiamiento from "@/views/Financiamiento";
import { mapState } from "vuex";

// Mapa section → feature flag para validar URL al restaurar.
// configuracion no tiene flag (siempre disponible).
// bancos y factoring comparten flag con financiamiento — son secciones
// independientes en el sidebar pero todas renderizadas por Financiamiento.vue.
const SECTION_FLAGS = Object.freeze({
    conciliador: "has_conciliator",
    accounts_plan: "has_classificator",
    classificator: "has_classificator",
    reports: "has_financial_report",
    reporte: "has_embedded_report",
    fileUpload: "has_file_upload",
    financiamiento: "has_financiamiento",
    bancos: "has_financiamiento",
    factoring: "has_financiamiento",
    configuracion: null,
});

// Secciones que renderizan el mismo componente Financiamiento (con texto
// específico según section query). Centralizado para evitar arrays mágicos
// repartidos por la template.
const FINANCIAMIENTO_SECTIONS = ["financiamiento", "bancos", "factoring"];

export default {
    name: "Dashboard",
    components: {
        Consolidation,
        MainLayout,
        Obligations,
        DashboardMainTabs,
        BankMovements,
        SectionHeader,
        DashboardBasic,
        DashboardFileUpload,
        DashboardClassificator,
        AccountsPlan,
        Configuracion,
        Financiamiento,
        DashboardReports
    },
    methods: {
        onChartRendered() {
            this.chartLoaded = true;
        },
        onSectionChange(section, tab) {
            this.visibleSection = section;
            const current = this.$route.query;
            const next = { ...current, section };
            if (tab) {
                next.tab = tab;
            } else {
                delete next.tab;
            }
            if (current.section === next.section && current.tab === next.tab) return;
            this.$router.replace({ query: next }).catch((err) => {
                if (err.name !== "NavigationDuplicated") throw err;
            });
        },
        isSectionValidForUser(section, user) {
            if (!(section in SECTION_FLAGS)) return false;
            const flag = SECTION_FLAGS[section];
            if (flag === null) return true;
            return Boolean(user[flag]);
        },
        pickDefaultSection(user) {
            // Orden de prioridad cuando no hay URL válida que restaurar.
            // bancos sustituye a financiamiento como entrada por default —
            // el sidebar ya no expone "financiamiento" como item clickeable,
            // los items reales son bancos y factoring (primero bancos).
            const order = ["conciliador", "classificator", "fileUpload", "reporte", "bancos"];
            const found = order.find((id) => {
                const flag = SECTION_FLAGS[id];
                return flag !== null && user[flag];
            });
            return found || "none";
        },
        setVisibleSectionFromUser(user) {
            if (!user) {
                this.visibleSection = "none";
                return;
            }
            // Side effect: prender los flags has_* que el template consume vía v-if.
            this.has_financiamiento = !!user.has_financiamiento;
            this.has_file_upload = !!user.has_file_upload;
            this.has_embedded_report = !!user.has_embedded_report;
            this.has_classificator = !!user.has_classificator;
            this.has_financial_report = !!user.has_financial_report;
            this.has_conciliator = !!user.has_conciliator;

            // Restaurar sección desde URL si es válida para este user; sino, default.
            const urlSection = this.$route.query.section;
            const targetSection = urlSection && this.isSectionValidForUser(urlSection, user)
                ? urlSection
                : this.pickDefaultSection(user);
            this.visibleSection = targetSection;

            // chartLoaded gating: sólo esperamos al onChartRendered de Consolidation
            // si vamos a renderizar el conciliador Y el user tiene embedded_report
            // (es el único caso donde DashboardBasic depende del chart inicializado).
            // Cualquier otra sección desbloquea clicks inmediatamente.
            const willWaitForChart = targetSection === "conciliador" && user.has_embedded_report;
            if (!willWaitForChart) {
                this.chartLoaded = true;
            }
        },
    },
    watch: {
        user: {
            handler(newValue) {
                if (newValue && newValue.user_info_loaded) {
                    this.setVisibleSectionFromUser(newValue);
                }
            },
            immediate: true,
        },
        // Navegación programática entre secciones (p.ej. el deep-link de
        // clasificación desde el drawer de reportes hace $router.push con
        // ?section=...). El sidebar sigue su camino vía onSectionChange — ese
        // handler setea visibleSection ANTES del replace, así este watcher ve
        // igualdad y no-opea (sin loop).
        "$route.query.section"(newSection) {
            if (!this.user || !this.user.user_info_loaded) return;
            if (!newSection || newSection === this.visibleSection) return;
            if (!this.isSectionValidForUser(newSection, this.user)) return;
            this.visibleSection = newSection;
            // Cualquier sección alcanzada por navegación ya no espera al chart
            // del conciliador.
            this.chartLoaded = true;
        },
    },
    computed: {
        ...mapState("userAuth", ["user"]),
        // bancos, factoring y el legacy financiamiento renderizan el mismo
        // componente — el view interno (Financiamiento.vue) decide el texto
        // según $route.query.section.
        isFinanciamientoSection() {
            return FINANCIAMIENTO_SECTIONS.includes(this.visibleSection);
        },
    },
    data() {
        return {
            visibleSection: '',
            has_conciliator: false,
            has_embedded_report: false,
            has_financiamiento: false,
            has_file_upload: false,
            has_classificator: false,
            has_financial_report: false,
            chartLoaded: false,
        };
    },
};

</script>
<style scoped>
.underHeader {
    position: relative;
}
</style>